/**
 * MitzvotList - Componente principal de lista de mandamientos
 * Lista con filtros, búsqueda y paginación
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getAllMitzvot, searchMitzvot, buildFilterParams } from '../../utils/mitzvot-api';
import MitzvotCard from './MitzvotCard';
import MitzvotFilters from './MitzvotFilters';
import Pagination from '../Common/Pagination';
import Loading from '../Common/Loading';
import { Search, Filter, List } from 'lucide-react';

const MitzvotList = () => {
  const [mitzvot, setMitzvot] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filtros y búsqueda
  const [filters, setFilters] = useState({
    search: '',
    tipo: '', // 'A' o 'L'
    perspectiva: '', // 'rambam', 'ramban', 'chinuch', 'semag', 'bahag', 'hebrewRoots'
    categorias: [], // Array de IDs de categorías de Hebrew Roots
    libro: '',
    parasha: '',
    minNumber: '',
    maxNumber: '',
    page: 1,
    limit: 20,
    orderBy: 'mitzvahNumber',
    orderDir: 'ASC'
  });
  
  // Paginación
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0
  });
  
  // UI State
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' o 'list'

  /**
   * Cargar mandamientos
   */
  const loadMitzvot = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = buildFilterParams(filters);
      const response = await getAllMitzvot(params);
      
      if (response.success) {
        setMitzvot(response.data || []);
        setPagination(response.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
      }
    } catch (err) {
      console.error('Error al cargar mandamientos:', err);
      setError('Error al cargar los mandamientos. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /**
   * Buscar mandamientos
   */
  const handleSearch = async (query) => {
    if (!query || query.trim().length === 0) {
      // Si no hay búsqueda, cargar lista normal
      setFilters(prev => ({ ...prev, search: '', page: 1 }));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await searchMitzvot(query, filters.limit);
      
      if (response.success) {
        setMitzvot(response.data || []);
        setPagination(response.pagination || {
          total: response.count || 0,
          page: 1,
          limit: filters.limit,
          totalPages: 1
        });
      }
    } catch (err) {
      console.error('Error en búsqueda:', err);
      setError('Error en la búsqueda. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Actualizar filtro
   */
  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset a página 1 si cambia filtro
    }));
  };

  /**
   * Limpiar filtros
   */
  const clearFilters = () => {
    setFilters({
      search: '',
      tipo: '',
      perspectiva: '',
      categorias: [],
      libro: '',
      parasha: '',
      minNumber: '',
      maxNumber: '',
      page: 1,
      limit: 20,
      orderBy: 'mitzvahNumber',
      orderDir: 'ASC'
    });
  };

  /**
   * Cambiar página
   */
  const handlePageChange = (newPage) => {
    updateFilter('page', newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cargar datos al montar y cuando cambien filtros
  useEffect(() => {
    loadMitzvot();
  }, [loadMitzvot]);

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Mandamientos (Mitzvot)
          </h1>
          <p className="text-gray-600">
            Explora los 962 mandamientos con perspectivas rabínicas
          </p>
        </div>
        
        {/* Total Count Badge */}
        {!loading && mitzvot.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">
              Resultados:
            </span>
            <div 
              className="flex items-center justify-center px-4 py-2 rounded-full shadow-md"
              style={{
                backgroundColor: '#10b981',
                minWidth: '60px'
              }}
            >
              <span className="text-white text-xl font-bold">
                {pagination.total}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Barra de búsqueda y controles */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Búsqueda */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar mandamientos..."
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch(filters.search);
                  }
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Controles */}
          <div className="flex gap-2">
            <button
              onClick={() => handleSearch(filters.search)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Buscar
            </button>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
                showFilters
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>

            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Panel de filtros */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <MitzvotFilters
              filters={filters}
              onFilterChange={updateFilter}
              onClearFilters={clearFilters}
            />
          </div>
        )}
      </div>

      {/* Resultados */}
      {loading ? (
        <Loading message="Cargando mandamientos..." />
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      ) : mitzvot.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-600 text-lg mb-2">No se encontraron mandamientos</p>
          <p className="text-gray-500 text-sm">
            Intenta ajustar los filtros o la búsqueda
          </p>
          <button
            onClick={clearFilters}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Limpiar filtros
          </button>
        </div>
      ) : (
        <>
          {/* Lista de mandamientos */}
          <div className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'flex flex-col gap-4'
          }>
            {mitzvot.map((mitzvah) => (
              <MitzvotCard
                key={mitzvah.id}
                mitzvah={mitzvah}
                viewMode={viewMode}
              />
            ))}
          </div>

          {/* Paginación */}
          {pagination.totalPages > 1 && (
            <div className="mt-8">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}

          {/* Info de resultados */}
          <div className="mt-4 text-center text-sm text-gray-600">
            Mostrando {mitzvot.length} de {pagination.total} mandamientos
          </div>
        </>
      )}
    </div>
  );
};

export default MitzvotList;



